import { getWeekDates, formatDateKey, type WeekInfo } from '@/utils/planning';

/**
 * Scrollable multi-week board ("Scroll door weken"): the selected week plus one
 * week before it and two after, laid out side by side. The arrows still move
 * the window one week at a time; the board opens scrolled to the selected week.
 */
export const MULTI_WEEK_BEFORE = 1;
export const MULTI_WEEK_AFTER = 2;

/** The weeks on the board: one week, or the multi-week window around `weekOffset`. */
export function getBoardWeeks(weekOffset: number, multiWeek: boolean): WeekInfo[] {
  if (!multiWeek) return [getWeekDates(weekOffset)];
  const weeks: WeekInfo[] = [];
  for (let o = weekOffset - MULTI_WEEK_BEFORE; o <= weekOffset + MULTI_WEEK_AFTER; o++) {
    weeks.push(getWeekDates(o));
  }
  return weeks;
}

/** All board days (Mon–Sat per week) in order, flattened. */
export function getBoardDays(weekOffset: number, multiWeek: boolean): Date[] {
  return getBoardWeeks(weekOffset, multiWeek).flatMap((w) => w.days);
}

/** The yyyy-mm-dd range the board fetches. */
export function getBoardRange(weekOffset: number, multiWeek: boolean): { from: string; to: string } {
  const days = getBoardDays(weekOffset, multiWeek);
  return { from: formatDateKey(days[0]), to: formatDateKey(days[days.length - 1]) };
}

/**
 * The `count` board days after `dateKey`, as yyyy-mm-dd. The board has no Sunday
 * column, so stretching a card one column to the right from a Saturday lands on
 * Monday; a plain +1 day would put it on an invisible Sunday.
 */
export function nextBoardDateKeys(dateKey: string, count: number): string[] {
  const d = new Date(`${dateKey}T12:00:00`);
  const keys: string[] = [];
  while (keys.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) keys.push(formatDateKey(d));
  }
  return keys;
}
